import React, { useRef, useEffect } from 'react';
import {
  Animated,
  Easing,
  View,
  ViewProps,
} from 'react-native';
import { BrandColor } from '@styleseat/brand';

type AnimatedBadgeProps = {
  /** True to start the entrance animation, false to start the exit animation */
  show?: boolean;
  /** True to skip initial animation. Used in storybook preview */
  skipAnimation?: boolean;
  appearance?: 'client' | 'pro';
  testID?: string;
};

/** A non-numeric, rectangular notification badge which animates into view */
const AnimatedBadge: React.FCWithChildren<AnimatedBadgeProps> = ({
  show,
  skipAnimation,
  appearance,
  testID,
}) => {
  const startValue = skipAnimation ? 1 : 0;
  const animatedOpacity = useRef<Animated.Value>(new Animated.Value(startValue)).current;
  const animatedScale = useRef<Animated.Value>(new Animated.Value(startValue)).current;

  const bounceIn = (): Animated.CompositeAnimation => {
    const result = Animated.parallel([
      Animated.timing(animatedOpacity, {
        duration: 100,
        easing: Easing.linear,
        toValue: 1,
        useNativeDriver: true,
      }),
      Animated.timing(animatedScale, {
        duration: 250,
        easing: Easing.bezier(0.16, 0.39, 0.54, 1.63),
        toValue: 1,
        useNativeDriver: true,
      }),
    ]);
    result.start();

    return result;
  };

  const bounceOut = (): Animated.CompositeAnimation => {
    const result = Animated.parallel([
      Animated.timing(animatedOpacity, {
        duration: 100,
        easing: Easing.linear,
        toValue: 0,
        useNativeDriver: true,
      }),
      Animated.timing(animatedScale, {
        duration: 250,
        easing: Easing.bezier(0.16, 0.39, 0.54, 1.63),
        toValue: 0,
        useNativeDriver: true,
      }),
    ]);
    result.start();
    return result;
  };

  useEffect(() => {
    const animation: Animated.CompositeAnimation = show ? bounceIn() : bounceOut();
    return () => animation.stop();
  }, [show]);

  const style: Animated.AnimatedProps<ViewProps>['style'] = {
    backgroundColor: BrandColor.Red,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: animatedOpacity,
    transform: [{ scale: animatedScale }],
  };

  return (
    appearance === 'client' ? (
      <Animated.View
        style={{
          ...style,
          height: 8,
          width: 8,
          borderRadius: 4,
        }}
        testID={testID}
      />
    ) : (
      <Animated.View
        style={{
          ...style,
          height: 16,
          width: 16,
          borderRadius: 3,
        }}
      >
        <View
          testID={testID}
          style={{
            width: 5,
            height: 5,
            borderRadius: 2,
            backgroundColor: BrandColor.WhiteWash,
          }}
        />
      </Animated.View>
    )
  );
};

export default AnimatedBadge;
